import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Divider, Flex, Input, Modal, Radio, Select, Spin, Table } from "antd";
import {
  DELETE_REASON_MAX_LEN,
  DELETE_REASON_OTHER,
  DELETE_REASON_PRESETS,
  resolveDeleteReason,
  type DeleteReasonPreset,
} from "./deleteReason";
import Column from "antd/es/table/Column";
import type { FilterValue, SorterResult, TableCurrentDataSource, TablePaginationConfig } from "antd/es/table/interface";
import { LeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { formatOrdinal, PATH_INFO, shouldStopForInvalidContextFetchResult } from "../../../util/helper";
import { CompanyContext, UpdateCompanyContext } from "../../../../context/companyContext";
import { clientFetch } from "../../../../utils/clientFetch";
import { useTranslation } from "react-i18next";

type SampleType = {
  id: string;
  created_at: string | null;
  created_by: string | null;
  part_number: string | null;
  sample_number: string | null;
  kind: string | null;
  time: string | null;
  device_name: string | null;
  device_location: string | null;
  department_name: string | null;
  brand_name: string | null;
  sampleImages: sampleImages[];
  url: string | null;
};

type sampleImages = {
  sample_id: string;
  image_index: number;
  url: string;
};

type DeleteContext = {
  mode: "single" | "bulk";
  targetIds: string[];
};

type FilterType = "all" | "filled" | "unfilled";

// BEのファイル名が取得できない/破損している場合のフォールバック
const DEFAULT_MEASUREMENT_EXPORT_FILENAME = "measurement-exports.zip";
// CSVエクスポートのタイムアウト時間（ミリ秒）
const MEASUREMENT_EXPORT_TIMEOUT_MS = 15 * 60 * 1000;
// CSVエクスポートの上限件数
const MEASUREMENT_EXPORT_MAX_COUNT = 100;
export function CompanyMeasurementsList() {
  const { t, i18n } = useTranslation();
  const { company } = useContext(CompanyContext);
  const { refreshCompany } = useContext(UpdateCompanyContext);
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [samples, setSamples] = useState<SampleType[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedFilter, setSelectedFilter] = useState<FilterType>("all");
  const [loading, setLoading] = useState(false);
  const [currentSortField, setCurrentSortField] = useState<string | undefined>(undefined);
  const [currentSortOrder, setCurrentSortOrder] = useState<"ascend" | "descend" | undefined>(undefined);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteContext, setDeleteContext] = useState<DeleteContext | null>(null);
  // 削除理由: プリセット選択と自由記述 (プリセット「その他」時のみ自由記述を採用)
  const [deleteReasonPreset, setDeleteReasonPreset] = useState<DeleteReasonPreset | null>(null);
  const [deleteReasonFreeText, setDeleteReasonFreeText] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [exportProcessing, setExportProcessing] = useState(false);
  const [isPageReady, setIsPageReady] = useState(false);
  const filterOptions = useMemo(() => [
    { label: t("measurements.all"), value: "all" },
    { label: t("measurements.filled"), value: "filled" },
    { label: t("measurements.unfilled"), value: "unfilled" },
  ], [t]);
  const deleteReasonPresetOptions = useMemo(() => (
    DELETE_REASON_PRESETS.map((value) => ({
      label: t(`measurements.deleteReason.presets.${value}`),
      value,
    }))
  ), [t]);

  const clearSortState = useCallback(() => {
    setCurrentSortField(undefined);
    setCurrentSortOrder(undefined);
  }, []);

  const toApiSortOrder = (antdOrder?: "ascend" | "descend") => {
    if (!antdOrder) return undefined;
    return antdOrder === "ascend" ? "ASC" : "DESC";
  };

  useEffect(() => {
    let isCancelled = false;

    const validateAndLoadMeasurements = async () => {
      setIsPageReady(false);

      if (!companyId) {
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
        return;
      }

      try {
        const companyResult = await refreshCompany(companyId);

        if (isCancelled) {
          return;
        }

        if (shouldStopForInvalidContextFetchResult(companyResult, navigate)) {
          return;
        }

        setIsPageReady(true);
      } catch (error) {
        console.error("Error validating company measurements list.", error);
        if (isCancelled) {
          return;
        }
        navigate(PATH_INFO.ERROR.PATH, { replace: true });
      }
    };

    validateAndLoadMeasurements();

    return () => {
      isCancelled = true;
    };
  }, [companyId, navigate, refreshCompany]);

  const fetchSamples = useCallback(
    async (page: number, size: number, filter: FilterType, sortField?: string, sortOrder?: string) => {
      if (!companyId) {
        return;
      }

      setLoading(true);

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          pageSize: size.toString(),
          filter,
        });

        if (sortField && sortOrder) {
          params.append("sortField", sortField);
          params.append("sortOrder", sortOrder);
        }

        const res = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/samples/company/${companyId}?${params.toString()}`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) {
          if (res.status === 404) {
            setSamples([]);
            setTotal(0);
            return;
          }

          throw new Error("Failed to fetch samples");
        }

        const payload = await res.json();
        const data: SampleType[] = Array.isArray(payload) ? payload : payload?.data ?? [];
        const totalItems = Array.isArray(payload) ? data.length : payload?.pagination?.total ?? data.length;

        setSamples(data);
        setTotal(totalItems);
      } catch (error) {
        console.error("Error fetching samples", error);
        setSamples([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    }, [companyId]);

  useEffect(() => {
    if (!companyId || !isPageReady) {
      return;
    }

    fetchSamples(currentPage, pageSize, selectedFilter, currentSortField, toApiSortOrder(currentSortOrder));
  }, [companyId, currentPage, pageSize, selectedFilter, currentSortField, currentSortOrder, fetchSamples, isPageReady]);

  const handleFilterChange = useCallback((value: FilterType) => {
    setCurrentPage(1);
    setSelectedFilter(value);
  }, []);

  const handlePaginationChange = useCallback((page: number, size: number) => {
    setCurrentPage(page);
    if (size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  const handleShowSizeChange = useCallback((_current: number, size: number) => {
    setCurrentPage(1);
    setPageSize(size);
  }, []);

  const handleTableChange = useCallback(
    (
      _pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<SampleType> | SorterResult<SampleType>[],
      _extra: TableCurrentDataSource<SampleType>
    ) => {
      // Table自体のonChangeでは「ソート操作」だけに反応する。ページングはpaginationのonChange/onShowSizeChangeで処理する。
      const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;

      if (activeSorter?.field && activeSorter.order) {
        const resolvedField = typeof activeSorter.field === "string" ? activeSorter.field : undefined;

        if (!resolvedField) {
          clearSortState();
          setCurrentPage(1);
          return;
        }

        const nextAntdOrder: "ascend" | "descend" = activeSorter.order === "ascend" ? "ascend" : "descend";
        const hasSortChanged = currentSortField !== resolvedField || currentSortOrder !== nextAntdOrder;

        setCurrentSortField(resolvedField);
        setCurrentSortOrder(nextAntdOrder);

        if (hasSortChanged) {
          setCurrentPage(1);
        }
        return;
      }

      if (activeSorter?.field && !activeSorter.order) {
        clearSortState();
        setCurrentPage(1);
        return;
      }

      if (!activeSorter?.field && (currentSortField || currentSortOrder)) {
        clearSortState();
        setCurrentPage(1);
      }
    },
    [clearSortState, currentSortField, currentSortOrder]
  );

  const deleteSample = useCallback(async (sampleId: string, reason: string | null) => {
    const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/samples/${sampleId}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });

    if (response.status === 401) {
      throw new Error(i18n.t("departmentUser.messages.authenticationFailed"));
    }

    if (!response.ok) {
      throw new Error(i18n.t("measurements.deleteFailedRetry"));
    }
  }, [i18n]);

  const deleteSamplesBulk = useCallback(async (ids: string[], reason: string | null) => {
    const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/samples`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ids, reason }),
    });

    if (response.status === 401) {
      throw new Error(i18n.t("departmentUser.messages.authenticationFailed"));
    }

    if (!response.ok) {
      throw new Error(i18n.t("measurements.deleteFailedRetry"));
    }
  }, [i18n]);

  const openSingleDeleteConfirm = useCallback((id: string) => {
    setDeleteContext({
      mode: "single",
      targetIds: [id],
    });
    setDeleteModalOpen(true);
  }, []);

  const renderText = useCallback((value: any) => (value !== undefined && value !== null && value !== "" ? value : t("measurements.notEntered")), [t]);
  const ALLOWED_URL_PROTOCOLS = useMemo(() => new Set(["http:", "https:"]), []);
  const getSafeUrl = useCallback((rawUrl: SampleType["url"]) => {
    if (!rawUrl || rawUrl.trim() === "") {
      return undefined;
    }

    try {
      const parsed = new URL(rawUrl);
      if (!ALLOWED_URL_PROTOCOLS.has(parsed.protocol)) {
        return undefined;
      }
      return parsed.toString();
    } catch (_error) {
      return undefined;
    }
  }, [ALLOWED_URL_PROTOCOLS]);
  const renderDeviceInfo = useCallback(
    (name: SampleType["device_name"], location: SampleType["device_location"]) => {
      const baseText = renderText(name);
      if (location && location.trim() !== "") {
        return `${baseText} (${location})`;
      }
      return baseText;
    },
    [renderText]
  );

  const handleSelectAll = useCallback(() => {
    if (!samples.length) {
      return;
    }

    setSelectedRowKeys((prev) => {
      const pageIds = samples.map((sample) => sample.id);
      const areAllSelected = pageIds.every((id) => prev.includes(id));

      if (areAllSelected) {
        return prev.filter((key) => !pageIds.includes(key));
      }

      const mergedKeys = new Set([...prev, ...pageIds]);
      return Array.from(mergedKeys);
    });
  }, [samples]);

  const handleBulkDeleteRequest = useCallback(() => {
    if (!selectedRowKeys.length) {
      return;
    }
    setDeleteContext({
      mode: "bulk",
      targetIds: selectedRowKeys,
    });
    setDeleteModalOpen(true);
  }, [selectedRowKeys]);

  // 削除理由 state を初期化する
  const resetDeleteReason = useCallback(() => {
    setDeleteReasonPreset(null);
    setDeleteReasonFreeText("");
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteContext) {
      return;
    }

    setDeleteProcessing(true);

    // プリセット/自由記述から削除理由を解決 (理由は任意。null 可)
    const reason = resolveDeleteReason(deleteReasonPreset, deleteReasonFreeText);

    try {
      if (deleteContext.mode === "bulk") {
        await deleteSamplesBulk(deleteContext.targetIds, reason);
      } else {
        await deleteSample(deleteContext.targetIds[0], reason);
      }

      setSelectedRowKeys((prev) => prev.filter((key) => !deleteContext.targetIds.includes(key)));
      await fetchSamples(currentPage, pageSize, selectedFilter, currentSortField, toApiSortOrder(currentSortOrder));
    } catch (error) {
      console.error("Error deleting samples", error);
      const fallbackMessage = t("measurements.deleteFailedRetry");
      if (error instanceof Error && error.message) {
        setFeedbackMessage(error.message);
      } else {
        setFeedbackMessage(fallbackMessage);
      }
    } finally {
      setDeleteProcessing(false);
      setDeleteModalOpen(false);
      setDeleteContext(null);
      resetDeleteReason();
    }
  }, [currentPage, currentSortField, currentSortOrder, deleteContext, deleteReasonFreeText, deleteReasonPreset, deleteSample, deleteSamplesBulk, fetchSamples, pageSize, resetDeleteReason, selectedFilter, toApiSortOrder, t]);

  const handleCancelDelete = useCallback(() => {
    if (deleteProcessing) {
      return;
    }
    setDeleteModalOpen(false);
    setDeleteContext(null);
    resetDeleteReason();
  }, [deleteProcessing, resetDeleteReason]);

  const handleFeedbackClose = useCallback(() => {
    setFeedbackMessage(null);
  }, []);

  const extractFilenameFromContentDisposition = useCallback((contentDisposition: string | null) => {
    if (!contentDisposition) {
      return undefined;
    }

    const utf8FilenameMatch = contentDisposition.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
    const asciiFilenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/i);
    const rawFilename = (utf8FilenameMatch?.[1] ?? asciiFilenameMatch?.[1])?.trim();
    if (!rawFilename) {
      return undefined;
    }

    const unquotedFilename = rawFilename.replace(/^["']|["']$/g, "");
    try {
      const decodedFilename = decodeURIComponent(unquotedFilename);
      return decodedFilename.replace(/[\\/:*?"<>|]/g, "_");
    } catch (_error) {
      return unquotedFilename.replace(/[\\/:*?"<>|]/g, "_");
    }
  }, []);

  const handleBulkExport = useCallback(async () => {
    if (!selectedRowKeys.length) {
      return;
    }

    const exportSampleIds = selectedRowKeys.map((id) => String(id).trim()).filter((id) => id.length > 0);
    if (exportSampleIds.length === 0) {
      Modal.warning({ content: t("measurements.invalidSelection"), centered: true });
      return;
    }

    if (exportSampleIds.length > MEASUREMENT_EXPORT_MAX_COUNT) {
      Modal.error({ title: t("measurements.outputError"), content: t("measurements.exportLimitExceeded", { count: MEASUREMENT_EXPORT_MAX_COUNT }), centered: true });
      return;
    }

    setExportProcessing(true);

    let timeoutId: number | undefined;
    try {
      const abortController = new AbortController();
      timeoutId = window.setTimeout(() => {
        abortController.abort();
      }, MEASUREMENT_EXPORT_TIMEOUT_MS);

      const response = await clientFetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/measurement-exports`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        signal: abortController.signal,
        body: JSON.stringify({ sample_ids: exportSampleIds }),
      });

      if (!response.ok) {
        throw new Error(t("measurements.exportFailed"));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers.get("Content-Disposition");
      const filename = extractFilenameFromContentDisposition(contentDisposition) ?? DEFAULT_MEASUREMENT_EXPORT_FILENAME;
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        Modal.error({ content: t("messages.csvDownloadFailed"), centered: true });
        return;
      }
      console.error("Error exporting measurement CSV with images", error);
      Modal.error({ content: t("messages.csvDownloadFailed"), centered: true });
    } finally {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      setExportProcessing(false);
    }
  }, [extractFilenameFromContentDisposition, selectedRowKeys, t]);

  const handleClearSelection = useCallback(() => {
    setSelectedRowKeys([]);
  }, []);
  const handleRowCheckboxChange = useCallback((id: string, checked: boolean) => {
    setSelectedRowKeys((prev) => {
      if (checked) {
        if (prev.includes(id)) {
          return prev;
        }
        return [...prev, id];
      }

      return prev.filter((key) => key !== id);
    });
  }, []);
  const selectedRowKeySet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);
  const currentPageSelectedCount = useMemo(
    () => samples.reduce((count, sample) => (selectedRowKeySet.has(sample.id) ? count + 1 : count), 0),
    [samples, selectedRowKeySet]
  );
  const selectedCount = selectedRowKeys.length;
  const hasSelection = selectedCount > 0;
  const isCurrentPageFullySelected = samples.length > 0 && currentPageSelectedCount === samples.length;
  const isCurrentPagePartiallySelected = currentPageSelectedCount > 0 && !isCurrentPageFullySelected;
  const isCurrentPageSelectionActive = currentPageSelectedCount > 0;
  const selectButtonLabel = isCurrentPageSelectionActive ? t("measurements.clearSelection") : t("measurements.selectAll");
  const handleSelectionToggle = useCallback(() => {
    if (isCurrentPageSelectionActive) {
      handleClearSelection();
      return;
    }
    handleSelectAll();
  }, [handleClearSelection, handleSelectAll, isCurrentPageSelectionActive]);

  if (!isPageReady) {
    return (
      <Flex justify="center" align="center" className="min-h-[50vh]">
        <Spin size="large" tip={t("common.loadingWithDots")} />
      </Flex>
    );
  }

  return (
    <div>
      <Button variant="text" color="primary" icon={<LeftOutlined />} onClick={() => navigate(-1)} className="mb-2 -ml-3">{t("common.back")}</Button>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2 break-all">
          {t("measurements.listPage", { companyName: company?.company_name ?? "" })}
        </p>
      </div>
      <Divider />
      <Flex align="center" className="w-full mb-2 ml-4 max-[1130px]:flex-col max-[1130px]:items-start" gap='large'>
        <Radio.Group
          onChange={(v) => handleFilterChange(v.target.value)}
          options={filterOptions}
          value={selectedFilter}
          optionType="button"
          buttonStyle="solid"
        />
        <Flex align="center" gap="small" className="max-[1130px]:w-full max-[1130px]:flex-wrap">
          <Checkbox
            className="selection-indicator-checkbox"
            checked={isCurrentPageFullySelected}
            disabled={!hasSelection}
            indeterminate={isCurrentPagePartiallySelected}
            aria-readonly
            tabIndex={-1}
          >
            {t("measurements.selectedCount", { count: selectedCount })}
          </Checkbox>
          <Button
            variant="outlined"
            color={isCurrentPageSelectionActive ? "default" : "primary"}
            disabled={!samples.length}
            onClick={handleSelectionToggle}
          >
            {selectButtonLabel}
          </Button>
          <Button
            variant="outlined"
            color="danger"
            disabled={!hasSelection}
            loading={deleteProcessing && deleteContext?.mode === "bulk"}
            onClick={handleBulkDeleteRequest}
          >
            {t("measurements.deleteSelected")}
          </Button>
          <Button
            variant="outlined"
            color="blue"
            disabled={!hasSelection}
            loading={exportProcessing}
            onClick={handleBulkExport}
          >
            {t("measurements.exportSelectedCsv")}
          </Button>
        </Flex>
      </Flex>
      <Flex vertical justify="center" className="relative">
        <Table<SampleType>
          dataSource={samples}
          bordered
          loading={loading}
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            position: ["bottomCenter"],
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (totalCount, range) => t("measurements.showTotal", { total: totalCount, start: range[0], end: range[1] }),
            pageSizeOptions: ["5", "10", "20", "50", "100"],
            onChange: handlePaginationChange,
            onShowSizeChange: handleShowSizeChange,
          }}
          rowKey={(record) => record.id}
        >
          <Column
            title=""
            key="selection"
            fixed="left"
            width={48}
            render={(_, record) => (
              <Checkbox
                checked={selectedRowKeys.includes(record.id)}
                onChange={(event) => handleRowCheckboxChange(record.id, event.target.checked)}
              />
            )}
          />
          <Column
            title={t("measurements.thumbnail")}
            dataIndex="sampleImages"
            key="sampleImages"
            className="w-[110px]"
            render={(value) => (
              <img src={value[0]?.url} alt="img" className="max-w-full max-h-full object-cover" />
            )}
          />
          <Column
            title={t("measurements.measuredAt")}
            dataIndex="created_at"
            key="created_at"
            className="w-[170px]"
            sorter
            sortOrder={currentSortField === "created_at" ? currentSortOrder : undefined}
            render={(value) => (value ? dayjs(value).format("YYYY/MM/DD HH:mm") : t("measurements.notEntered"))}
          />
          <Column
            title='URL'
            dataIndex='url'
            key='url'
            className="w-[179px]"
            render={(value: SampleType["url"]) => (
              <div className="w-[140px] whitespace-normal break-all text-[10px] leading-[14px]">
                {getSafeUrl(value) ? (
                  <a
                    href={getSafeUrl(value)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={t("measurements.openUrlNewTab")}
                    aria-label={t("measurements.openUrlNewTab")}
                  >{renderText(value)}</a>
                ) : (
                  <span>{renderText(value)}</span>
                )}
              </div>
            )}
          />
          <Column
            title={t("company.departmentName")}
            dataIndex='department_name'
            key='department_name'
            sorter
            sortOrder={currentSortField === "department_name" ? currentSortOrder : undefined}
            render={renderText}
          />
          <Column
            title={t("measurements.recorder")}
            dataIndex="created_by"
            key="created_by"
            sorter
            sortOrder={currentSortField === "created_by" ? currentSortOrder : undefined}
            render={renderText}
          />
          <Column
            title={t("itemMaster.partNumber")}
            dataIndex='part_number'
            key='part_number'
            render={renderText}
          />
          <Column
            title={t("measurements.type")}
            dataIndex='kind'
            key='kind'
            render={(value) => value ? value === 'sample' ? t("measurements.sample") : t("measurements.final") : t("measurements.notEntered")}
          />
          <Column
            title={t("measurements.measurement")}
            dataIndex="time"
            key="time"
            className="w-[70px]"
            render={(value) => (value ? formatOrdinal(parseInt(value, 10)) : t("measurements.notEntered"))}
          />
          <Column
            title={t("company.brandName")}
            dataIndex="brand_name"
            key="brand_name"
            sorter
            sortOrder={currentSortField === "brand_name" ? currentSortOrder : undefined}
            render={renderText}
          />
          <Column
            title={t("device.deviceName")}
            dataIndex="device_name"
            key="device_name"
            render={(_, record) => renderDeviceInfo(record.device_name, record.device_location)}
          />
          <Column
            title={t("common.operation")}
            dataIndex="action"
            key="action"
            render={(_: any, record) => (
              <Button
                variant="outlined"
                color="danger"
                onClick={() => openSingleDeleteConfirm(record.id)}
                loading={
                  deleteProcessing &&
                  deleteContext?.mode === "single" &&
                  Boolean(deleteContext?.targetIds?.includes(record.id))
                }
              >
                {t("common.delete")}
              </Button>
            )}
            fixed='right'
            width="100px"
          />
        </Table>
      </Flex>
      <Modal
        open={deleteModalOpen}
        title={t("measurements.deleteConfirmTitle")}
        okText={t("measurements.deleteOk")}
        okButtonProps={{ danger: true, loading: deleteProcessing }}
        cancelButtonProps={{ disabled: deleteProcessing }}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
      >
        <p>
          {deleteContext?.mode === "bulk"
            ? t("measurements.bulkDeleteConfirm", { count: deleteContext?.targetIds.length ?? 0 })
            : t("measurements.singleDeleteConfirm")}
        </p>
        <Flex vertical gap="small" className="mt-2">
          <span className="text-sm text-gray-600">{t("measurements.deleteReason.label")}</span>
          <Select<DeleteReasonPreset>
            placeholder={t("measurements.deleteReason.selectPlaceholder")}
            allowClear
            value={deleteReasonPreset ?? undefined}
            options={deleteReasonPresetOptions}
            onChange={(value) => setDeleteReasonPreset(value ?? null)}
            disabled={deleteProcessing}
          />
          {deleteReasonPreset === DELETE_REASON_OTHER && (
            <Input.TextArea
              value={deleteReasonFreeText}
              onChange={(event) => setDeleteReasonFreeText(event.target.value)}
              placeholder={t("measurements.deleteReason.freeTextPlaceholder")}
              maxLength={DELETE_REASON_MAX_LEN}
              showCount
              autoSize={{ minRows: 2, maxRows: 4 }}
              disabled={deleteProcessing}
            />
          )}
        </Flex>
      </Modal>
      <Modal
        open={Boolean(feedbackMessage)}
        footer={[
          <Button key="close" type="primary" onClick={handleFeedbackClose}>
            {t("common.close")}
          </Button>,
        ]}
        onCancel={handleFeedbackClose}
      >
        <p>{feedbackMessage}</p>
      </Modal>
    </div>
  );
}
